import React, { useState, useEffect } from 'react';
import { Save, X, User, Mail, Phone, Briefcase, Award, DollarSign } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';

const StaffForm = ({ staff, onSave, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    gender: 'Male',
    role: 'doctor',
    specializations: [],
    qualifications: [],
    experienceYears: 0,
    consultationFee: 0,
    bio: '',
    dateOfBirth: '',
    dateOfJoining: new Date().toISOString().split('T')[0],
    isActive: true,
    dateOfExit: ''
  });

  const [specializationInput, setSpecializationInput] = useState('');
  const [qualificationInput, setQualificationInput] = useState('');

  useEffect(() => {
    if (staff) {
      setFormData({
        fullName: staff.fullName || '',
        email: staff.email || '',
        phone: staff.phone || '',
        gender: staff.gender || 'Male',
        role: staff.role || 'doctor',
        specializations: staff.specializations || [],
        qualifications: staff.qualifications || [],
        experienceYears: staff.experienceYears || 0,
        consultationFee: staff.consultationFee || 0,
        bio: staff.bio?.summary || '',
        dateOfBirth: staff.dateOfBirth || '',
        dateOfJoining: staff.dateOfJoining || new Date().toISOString().split('T')[0],
        isActive: staff.isActive !== undefined ? staff.isActive : true,
        dateOfExit: staff.dateOfExit || ''
      });
    }
  }, [staff]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addSpecialization = () => {
    if (specializationInput.trim()) {
      setFormData(prev => ({
        ...prev,
        specializations: [...prev.specializations, specializationInput.trim()]
      }));
      setSpecializationInput('');
    }
  };

  const removeSpecialization = (index) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.filter((_, i) => i !== index)
    }));
  };

  const addQualification = () => {
    if (qualificationInput.trim()) {
      setFormData(prev => ({
        ...prev,
        qualifications: [...prev.qualifications, qualificationInput.trim()]
      }));
      setQualificationInput('');
    }
  };

  const removeQualification = (index) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate active/inactive status
      if (staff && !formData.isActive && !formData.dateOfExit) {
        throw new Error('Date of exit is required when marking staff as inactive');
      }

      // Validate dateOfExit is not before dateOfJoining
      if (formData.dateOfExit && formData.dateOfJoining) {
        if (new Date(formData.dateOfExit) < new Date(formData.dateOfJoining)) {
          throw new Error('Date of exit cannot be before date of joining');
        }
      }

      if (staff) {
        // Update existing staff
        await updateStaff();
      } else {
        // Create new staff
        await createStaff();
      }

      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save staff member');
      console.error('Error saving staff:', err);
    } finally {
      setLoading(false);
    }
  };

  const createStaff = async () => {
    // Step 1: Create user profile
    const { data: userProfile, error: profileError } = await supabase
      .from('user_profiles')
      .insert({
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        gender: formData.gender,
        date_of_birth: formData.dateOfBirth || null,
        role: formData.role
      })
      .select()
      .single();

    if (profileError) throw profileError;

    // Step 2: Create staff record
    const { error: staffError } = await supabase
      .from('staff')
      .insert({
        user_id: userProfile.id,
        role: formData.role,
        gender: formData.gender,
        specializations: formData.specializations,
        qualifications: formData.qualifications,
        experience_years: parseInt(formData.experienceYears),
        consultation_fee: parseFloat(formData.consultationFee),
        bio: { summary: formData.bio },
        date_of_joining: formData.dateOfJoining,
        is_active: true
      });

    if (staffError) throw staffError;
  };

  const updateStaff = async () => {
    // Update user profile
    const { error: profileError } = await supabase
      .from('user_profiles')
      .update({
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        gender: formData.gender,
        date_of_birth: formData.dateOfBirth || null
      })
      .eq('id', staff.userId);

    if (profileError) throw profileError;

    // Update staff record
    const { error: staffError } = await supabase
      .from('staff')
      .update({
        gender: formData.gender,
        specializations: formData.specializations,
        qualifications: formData.qualifications,
        experience_years: parseInt(formData.experienceYears),
        consultation_fee: parseFloat(formData.consultationFee),
        bio: { summary: formData.bio },
        is_active: formData.isActive,
        date_of_exit: formData.dateOfExit || null
      })
      .eq('id', staff.id);

    if (staffError) throw staffError;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Basic Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Full Name *
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => handleChange('fullName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4 inline mr-2" />
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4 inline mr-2" />
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gender *
            </label>
            <select
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            >
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date of Birth
            </label>
            <input
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date of Joining *
            </label>
            <input
              type="date"
              value={formData.dateOfJoining}
              onChange={(e) => handleChange('dateOfJoining', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
        </div>

        {/* Employment Status */}
        {staff && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                Active Staff Member
              </label>
            </div>
            {!formData.isActive && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Exit *
                </label>
                <input
                  type="date"
                  value={formData.dateOfExit}
                  onChange={(e) => handleChange('dateOfExit', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  min={formData.dateOfJoining}
                  required
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Professional Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Professional Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Briefcase className="w-4 h-4 inline mr-2" />
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
              disabled={!!staff}
            >
              <option value="doctor">Doctor</option>
              <option value="therapist">Therapist</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Award className="w-4 h-4 inline mr-2" />
              Experience (Years) *
            </label>
            <input
              type="number"
              min="0"
              value={formData.experienceYears}
              onChange={(e) => handleChange('experienceYears', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="w-4 h-4 inline mr-2" />
              Consultation Fee (₹) *
            </label>
            <input
              type="number"
              min="0"
              step="50"
              value={formData.consultationFee}
              onChange={(e) => handleChange('consultationFee', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
        </div>

        {/* Bio */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bio / Summary
          </label>
          <textarea
            value={formData.bio}
            onChange={(e) => handleChange('bio', e.target.value)}
            rows="3"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="Brief professional summary..."
          />
        </div>
      </div>

      {/* Specializations */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Specializations</h3>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={specializationInput}
            onChange={(e) => setSpecializationInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSpecialization();
              }
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="Add specialization..."
          />
          <button
            type="button"
            onClick={addSpecialization}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.specializations.map((spec, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full flex items-center gap-2"
            >
              {spec}
              <button
                type="button"
                onClick={() => removeSpecialization(index)}
                className="text-purple-500 hover:text-purple-700"
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Qualifications */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Qualifications</h3>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={qualificationInput}
            onChange={(e) => setQualificationInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addQualification();
              }
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="Add qualification..."
          />
          <button
            type="button"
            onClick={addQualification}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.qualifications.map((qual, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-green-100 text-green-700 rounded-full flex items-center gap-2"
            >
              {qual}
              <button
                type="button"
                onClick={() => removeQualification(index)}
                className="text-green-500 hover:text-green-700"
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
        >
          <X className="w-5 h-5" />
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              {staff ? 'Update Staff' : 'Create Staff'}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default StaffForm;

